import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ShieldCheck, Terminal, ArrowRight, Sparkles, Send } from 'lucide-react';
import { mockData } from '../mock/data';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        const response = await axios.post(`${API}/newsletter/subscribe`, {
          email: email
        });
        
        if (response.data.success) {
          toast({
            title: 'Successfully Subscribed!',
            description: 'You will receive our updates soon.',
          });
          setEmail('');
        } else {
          toast({
            title: 'Warning',
            description: response.data.message,
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Error subscribing',
          variant: 'destructive'
        });
      }
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'zap':
        return Zap;
      case 'shield-check':
        return ShieldCheck;
      case 'terminal':
        return Terminal;
      default:
        return Zap;
    }
  };

  return (
    <>
      <SEO 
        title="GENMOZPRO - Professional Payment Testing Tools for Developers"
        description="Professional suite for developers: BIN Generator, Check Live and Check Stripe. Test payment systems with precision using Luhn algorithm. Free tools for payment integration testing."
        keywords="bin generator, card checker, stripe connector, payment testing, luhn algorithm, credit card validator, developer tools, payment integration testing, card validation"
        canonical="https://genmozpro.com/"
      />
      <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/20 via-black to-teal-950/20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/10 via-transparent to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center pt-20 w-full">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-6 sm:mb-8">
            <Sparkles className="w-3 sm:w-4 h-3 sm:h-4 text-yellow-500" />
            <span className="text-yellow-500 text-xs sm:text-sm font-medium">Professional Testing Suite</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-4 sm:mb-6">
            <span className="text-yellow-500">GENMOZ</span>
            <span className="text-white">PRO</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
            Professional suite for developers and security researchers
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            <Link
              to="/gen"
              className="w-full sm:w-auto group flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 sm:py-4 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-all transform hover:scale-105"
            >
              <Zap className="w-4 sm:w-5 h-4 sm:h-5" />
              <span className="text-sm sm:text-base">Get Started</span>
            </Link>
            <Link
              to="/artigos"
              className="w-full sm:w-auto group flex items-center justify-center space-x-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-gray-700 text-white font-semibold rounded-lg hover:border-gray-500 transition-all"
            >
              <span className="text-sm sm:text-base">View Documentation</span>
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Email Subscribe */}
          <form onSubmit={handleSubscribe} className="max-w-md mx-auto px-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your best email"
                className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white placeholder-gray-500 transition-colors text-sm sm:text-base"
              />
              <button
                type="submit"
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-cyan-500 text-white font-medium rounded-lg hover:bg-cyan-400 transition-colors"
              >
                <Send className="w-4 sm:w-5 h-4 sm:h-5" />
                <span className="text-sm sm:text-base">Subscribe</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Ferramentas <span className="text-yellow-500">Profissionais</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Tudo que você precisa para testar sistemas de pagamento
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {mockData.tools.map((tool) => {
              const Icon = getIcon(tool.icon);
              return (
                <Link
                  key={tool.id}
                  to={tool.link}
                  className="group relative bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-all transform hover:scale-105"
                >
                  {/* Colored top border */}
                  <div className={`h-1 ${tool.bgColor}`}></div>

                  <div className="p-6 sm:p-8">
                    {/* Icon */}
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 ${tool.bgColor} rounded-lg flex items-center justify-center mb-4 sm:mb-6`}>
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>

                    {/* Label */}
                    <div className={`text-${tool.color}-500 text-xs font-bold mb-2 tracking-wider`}>
                      {tool.label}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                      {tool.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                      {tool.description}
                    </p>

                    {/* Link */}
                    <div className={`flex items-center space-x-2 text-${tool.color}-500 group-hover:translate-x-2 transition-transform`}>
                      <span className="font-medium text-sm sm:text-base">Acessar</span>
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${stat.color} mb-2`}>
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm sm:text-base lg:text-lg">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-4 sm:mb-6">
                <ShieldCheck className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500" />
                <span className="text-cyan-500 text-xs sm:text-sm font-medium">Sistema Online</span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
                Segurança e Confiabilidade
              </h2>
              <p className="text-gray-400 text-base sm:text-lg mb-6 sm:mb-8">
                Nossa plataforma é construída com os mais altos padrões de segurança, garantindo que seus testes sejam realizados de forma ética e protegida.
              </p>
              <ul className="space-y-3 sm:space-y-4">
                {mockData.securityFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-cyan-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-300 text-sm sm:text-base">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative order-1 lg:order-2">
              <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-cyan-900/20 to-purple-900/20 border border-gray-800">
                <img
                  src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format"
                  alt="Security"
                  className="w-full h-full object-cover opacity-60"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Pronto para começar?
          </h2>
          <p className="text-gray-400 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8">
            Join thousands of developers who trust our platform
          </p>
          <Link
            to="/gen"
            className="inline-flex items-center space-x-2 px-6 sm:px-8 py-3 sm:py-4 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-all transform hover:scale-105"
          >
            <Sparkles className="w-4 sm:w-5 h-4 sm:h-5" />
            <span className="text-sm sm:text-base">Get Started Free</span>
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;
